import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { firebaseAuth, reconcileAvatarFields, UserProfile, db } from '../services/firebase';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string, avatar: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  /** Anonymous guest sign-in. The user gets a real Firebase uid so socket
   *  auth + game persistence work. Profile is auto-created with a fun name. */
  signInAsGuest: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user profile when user changes
  const loadUserProfile = async (user: User | null) => {
    if (user) {
      try {
        const profile = await firebaseAuth.getUserProfile(user.uid);
        setUserProfile(profile);

        // Best-effort: ensure the friend search index has the latest
        // (uid, displayName, avatar) tuple so this user is findable by
        // others. Cheap upsert, never blocks loadUserProfile.
        if (profile?.displayName) {
          import('../services/friendsService').then(m =>
            m.upsertOwnIndexRow(user.uid, profile.displayName, profile.avatar || 'fox')
          ).catch(() => {});
        }

        // Reconcile the avatar fields on every login.
        //
        // The old code here only rescued profiles stuck on the emoji
        // defaults ('👤' / '🎮'). It could never fix the actual reported
        // bug, because the moment the setup screen saved its branded
        // default the stored avatar became 'fox' — which is not a "stuck
        // default" by that test, so the backfill skipped it and the Google
        // photo stayed invisible forever. The user's own account was in
        // exactly that state.
        //
        // reconcileAvatarFields() is total: it takes any legacy shape and
        // produces all four fields, including the derived `avatar` that the
        // rest of the app (App.tsx, GameContext, the socket handshake,
        // gameRecords, the results screen) already reads. We only write when
        // something actually changed, so this is a no-op on steady state.
        if (profile) {
          const av = reconcileAvatarFields(profile, user.photoURL);
          const changed =
            profile.avatar !== av.avatar ||
            profile.photoURL !== av.photoURL ||
            profile.brandedAvatar !== av.brandedAvatar ||
            profile.useGooglePhoto !== av.useGooglePhoto;

          if (changed) {
            const updated = { ...profile, ...av };
            // Paint immediately; don't wait on the Firestore round-trip.
            setUserProfile(updated);
            try {
              // Firestore rejects undefined values — strip photoURL when
              // the user has no Google photo (e.g. an email/guest account).
              const patch: Record<string, unknown> = {
                avatar: av.avatar,
                brandedAvatar: av.brandedAvatar,
                useGooglePhoto: av.useGooglePhoto,
              };
              if (av.photoURL) patch.photoURL = av.photoURL;
              await firebaseAuth.updateUserProfile(user.uid, patch);
            } catch (e) {
              console.warn('Avatar reconcile write failed (non-blocking):', e);
            }
          }
        }

        // If no profile exists in Firestore, create a basic one
        if (!profile && user.email) {
          console.log('📝 Creating basic user profile for:', user.email);
          const av = reconcileAvatarFields(null, user.photoURL);
          const basicProfile: UserProfile = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || 'Anonymous Player',
            avatar: av.avatar,
            ...(av.photoURL ? { photoURL: av.photoURL } : {}),
            brandedAvatar: av.brandedAvatar,
            useGooglePhoto: av.useGooglePhoto,
            createdAt: new Date(),
            lastLoginAt: new Date(),
            preferences: {
              soundEnabled: true,
              notificationsEnabled: true,
              theme: 'light',
              aiDifficulty: 'medium',
              aiPersonality: 'encouraging'
            },
            stats: {
              totalGames: 0,
              totalWins: 0,
              totalScore: 0,
              currentStreak: 0,
              longestStreak: 0,
              level: 1,
              experiencePoints: 0
            },
            achievements: []
          };
          
          // Try to create the profile, but don't fail if Firestore is unavailable
          await firebaseAuth.createUserProfile(user.uid, basicProfile);
          setUserProfile(basicProfile);
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
        setUserProfile(null);
      }
    } else {
      setUserProfile(null);
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    // Check if Firebase auth is enabled
    const isFirebaseEnabled = import.meta.env.VITE_ENABLE_FIREBASE_AUTH === 'true';
    
    if (!isFirebaseEnabled) {
      console.log('🚫 Firebase auth disabled - skipping auth initialization');
      setLoading(false);
      return;
    }

    const unsubscribe = firebaseAuth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      await loadUserProfile(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  /**
   * YOUR OWN PROFILE, LIVE. One document, one listener, for the whole app.
   *
   * `loadUserProfile` above reads `users/{uid}` exactly once per sign-in, which
   * meant every field the SERVER owns went stale the moment it was read: your
   * ranked score and career totals after a game, your subscription tier, a name
   * or avatar you changed on your phone while the laptop was open. Everything
   * downstream of `userProfile` — Profile, Settings, Plans, the default game
   * visibility Home reads — inherited that staleness.
   *
   * It reconciles the avatar fields on the way in, using the same total function
   * the initial load uses. That is deliberate: the initial load may WRITE a
   * reconciled avatar, and if this listener echoed the pre-write document back
   * unreconciled, the two would fight and the avatar would flicker. Running the
   * same idempotent function on both paths means they cannot disagree.
   */
  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid || !db) return;

    const unsub = onSnapshot(
      doc(db, 'users', uid),
      snap => {
        if (!snap.exists()) return;   // deletion is signOut's business, not ours
        const profile = snap.data() as UserProfile;
        const av = reconcileAvatarFields(profile, currentUser?.photoURL ?? null);
        setUserProfile({ ...profile, ...av });
      },
      err => {
        // A denied read here is not "no profile" — keep what we have and say so.
        console.warn('[AuthContext] profile snapshot error:', (err as any)?.code || err);
      },
    );

    return unsub;
  }, [currentUser?.uid, currentUser?.photoURL]);

  // Sign up function
  const signUp = async (email: string, password: string, displayName: string, avatar: string) => {
    try {
      await firebaseAuth.signUpWithEmail(email, password, displayName, avatar);
      // Profile will be loaded automatically via auth state change
    } catch (error) {
      throw error;
    }
  };

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      await firebaseAuth.signInWithEmail(email, password);
      // Profile will be loaded automatically via auth state change
    } catch (error) {
      throw error;
    }
  };

  // Google sign in function
  const signInWithGoogle = async () => {
    try {
      await firebaseAuth.signInWithGoogle();
      // Profile will be loaded automatically via auth state change
    } catch (error) {
      throw error;
    }
  };

  const signInAsGuest = async () => {
    try {
      await firebaseAuth.signInAnonymously();
      // Profile created inline in firebase.ts; auth state change will
      // hydrate userProfile via loadUserProfile.
    } catch (error) {
      throw error;
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      await firebaseAuth.signOut();
      setUserProfile(null);
    } catch (error) {
      throw error;
    }
  };

  // Reset password function
  const resetPassword = async (email: string) => {
    try {
      await firebaseAuth.resetPassword(email);
    } catch (error) {
      throw error;
    }
  };

  // Update user profile function
  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!currentUser) throw new Error('No user signed in');

    try {
      await firebaseAuth.updateUserProfile(currentUser.uid, updates);
      // Refresh the profile
      await refreshUserProfile();
      // Keep the friends search index up to date so a renamed user is
      // findable by the new name. Best-effort — index writes never block.
      try {
        const next = { ...(userProfile || {}), ...updates } as UserProfile;
        if (next.displayName) {
          const { upsertOwnIndexRow } = await import('../services/friendsService');
          await upsertOwnIndexRow(currentUser.uid, next.displayName, next.avatar);
        }
      } catch (e) {
        console.warn('Friend index refresh failed (non-blocking):', e);
      }
    } catch (error) {
      throw error;
    }
  };

  // Refresh user profile function
  const refreshUserProfile = async () => {
    if (!currentUser) return;
    
    try {
      const profile = await firebaseAuth.getUserProfile(currentUser.uid);
      setUserProfile(profile);
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  };

  // Admin role checking
  const isAdmin = useMemo(() => {
    if (!currentUser?.email) return false;
    
    // Simple admin list - in production, this would be in Firebase
    const adminEmails = [
      'arsalanjb@gmail.com',
      // Add more admin emails here
    ];
    
    return adminEmails.includes(currentUser.email);
  }, [currentUser?.email]);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInAsGuest,
    signOut,
    resetPassword,
    updateUserProfile,
    refreshUserProfile,
    isAdmin,
    isAuthenticated: !!currentUser,
    // Firebase flags anonymous users via .isAnonymous on the User object.
    isGuest: !!currentUser?.isAnonymous,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 